import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import {
  Box, ClickAwayListener, Grow, MenuItem, MenuList,
  Paper, Popper,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import SortIcon from '@mui/icons-material/Sort';
import { colors } from "../../common/colors";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import dayjs from "../../util/dayjs";
import { isAdmin } from "../util/util";
import ContainerPaper from "../components/ContainerPaper";
import LoadingModal from "../../components/LoadingModal";
import AdminPageFrame from "../components/AdminPageFrame";
import ReadMore from "../components/ReadMore";
import Separator from "../../components/Separator";
import FetchButton from "../components/FetchButton";
import Term, { TermRef } from "../components/Term";
import DetailsModal from "../components/userNotifications/DetailsModal";
import apiServices from "../services/apiServices";

export default function UserNotificationList() {
  const { admin } = useSelector((state: any) => state.adminAuth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /* 検索項目 */
  const [query, setQuery] = useState<string>("");
  const [sortOption, setSortOption] = useState<"asc" | "desc">("desc");
  const termRef = useRef<TermRef>(null);

  /* API関連 */
  const sortMenuOptions = [
    {value: "desc", name: T("mng_user_notifications_sort_desc")},
    {value: "asc", name: T("mng_user_notifications_sort_asc")},
  ]
  const [lastParams, setLastParams] = useState<any>();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});

  /* 画面制御 */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailsModalData, setDetailsModalData] = useState<any>();
  const [openDateSortOption, setOpenDateSortOption] = useState<boolean>(false);
  const dateAnchorRef = useRef<any>(null)

  const handleSelectSort = async (value: "asc" | "desc") => {
    setSortOption(value)
    setOpenDateSortOption(false)
  }

  useEffect(() => {
    if (!lastParams) return;

    const f = async () => {
      setIsLoading(true);

      const params = {
        ...lastParams,
        sort_direction: sortOption,
        page: 1
      };
      await getNotifications(params);

      setIsLoading(false);
    }
    f();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  useEffect(() => {
    handleSearch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* API関連 */
  const getNotifications = async (params: any) => {
    const _p = { ...params, sort_type: "sent_date" };

    try {
      const res = await apiServices.getUserNotifications(_p);
      if (res.pagination.current_page > 1) {
        setNotifications((prev: any[]) => [...prev, ...res.user_notifications]);
      } else {
        setNotifications(res.user_notifications);
      }
      setPagination(res.pagination);
      setLastParams(_p);
    } catch (error: any) {
      dispatch(setErrorObj(error))
    }
  };

  const handleSearch = async () => {
    if (!termRef?.current) return false;

    setIsLoading(true);

    const term = termRef.current.getTerm();
    const params = {
      start_date: term.startDate.format("YYYY-MM-DD"),
      end_date: term.endDate.format("YYYY-MM-DD"),
      term: query.replace(/^[@＠]/, ""),
      user_search: Boolean(query.startsWith("@") || query.startsWith("＠")),
      sort_direction: sortOption
    };
    await getNotifications(params);

    setIsLoading(false);

    return false;
  }

  const handleMore = async () => {
    if (!pagination.next_page || pagination.next_page < 1) return

    setIsLoading(true);

    const params = {
      ...lastParams,
      page: pagination.next_page
    };
    await getNotifications(params);

    setIsLoading(false);
  }

  /* 整形 */
  const dispUser = (user: any) => {
    return user?.nickname ?? user?.name;
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading}/>

      <ContainerPaper>
        <Typography sx={styles.search.title}>{T("mng_search_area_title")}</Typography>
        <Box sx={styles.search.inputs.container}>
          <Box>
            <Typography sx={styles.search.inputs.term.label}>
              {T("mng_user_notifications_search_term")}
            </Typography>

            <Term
              baseDate={dayjs()}
              vertical={false}
              ref={termRef}
            />
          </Box>

          <Box>
            <TextField
              placeholder={`${T("mng_user_notifications_search_query_placeholder")}`}
              value={query}
              onChange={(e: any) => setQuery(e.target.value)}
              fullWidth hiddenLabel variant="outlined"
            />
            <Typography variant="caption">{T("mng_user_notifications_search_query_note")}</Typography>
          </Box>
        </Box>
        <FetchButton
          title={T('mng_search')}
          onSubmit={handleSearch}
          validate={() => Promise.resolve(termRef.current?.validate() ?? false)}
          sx={styles.search.submit}
        />
      </ContainerPaper>

      <ContainerPaper>
        <Box sx={styles.result.header}>
          <Typography sx={styles.result.title}>{T("mng_user_notifications_data_title")}</Typography>
          {isAdmin(admin) && (
            <Box>
              <SendIcon onClick={() => navigate("/mng/create_user_notification")} />
              <Typography component="span" onClick={() => navigate("/mng/create_user_notification")}>
                {T("mng_user_notifications_send")}
              </Typography>
            </Box>
          )}
        </Box>

        <Separator color={colors.farm_gray100} />

        <Table aria-label="caption table" sx={styles.result.table.base}>
          <TableHead>
            <TableRow>
              <TableCell
                ref={dateAnchorRef}
                onClick={() => setOpenDateSortOption(true)}
                sx={styles.result.table.sentDate}
              >
                {T("mng_user_notifications_message_sent_date")}({T(`mng_user_notifications_sort_${sortOption}`)})
                <SortIcon sx={{color: colors.farm_blue200}}/>
              </TableCell>
              <TableCell sx={styles.result.table.title}>{T("mng_user_notifications_message_title")}</TableCell>
              <TableCell sx={styles.result.table.to}>{T("mng_user_notifications_message_to")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={styles.result.table.body}>
            {notifications.map((item: any, index: number) => (
              <TableRow key={index} onClick={() => setDetailsModalData(item)}>
                <TableCell sx={styles.result.table.sentDate}>
                  {dayjs(item.notification.created_at).format("YYYY/MM/DD HH:mm")}
                </TableCell>
                <TableCell sx={styles.result.table.title}>
                  {item.notification.title}
                </TableCell>
                <TableCell sx={styles.result.table.to}>
                  {dispUser(item.user)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ReadMore open={pagination.next_page > 0} sx={styles.result.pagination} onClick={() => handleMore()} />
      </ContainerPaper>

      <DetailsModal
        open={Boolean(detailsModalData)}
        data={detailsModalData}
        onClose={() => setDetailsModalData(undefined)}
      />

      <Popper
        open={openDateSortOption}
        anchorEl={dateAnchorRef.current}
        transition
      >
        {({ TransitionProps}) => (
          <Grow {...TransitionProps} style={{transformOrigin:'top'}}>
            <Paper>
              <ClickAwayListener onClickAway={() => setOpenDateSortOption(false)}>
                <MenuList>
                  {sortMenuOptions.map((option: any) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleSelectSort(option.value)}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </AdminPageFrame>
  );
}

const styles = {
  search: {
    title: {
      paddingY: 2,
    },
    inputs: {
      container: {
        display: "flex",
        flexDirection: "column",
        gap: 2
      },
      term: {
        label: {
          color: "rgba(0, 0, 0, .6)",
          paddingRight: 1
        }
      }
    },
    submit: {
      display: "flex",
      justifyContent: "flex-end"
    }
  },
  result: {
    container: {
      padding: 2,
      margin: 3
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 2,
      lineHeight: "120px"
    },
    title: {
      fontWeight: 700,
      fontSize: 16,
    },
    table: {
      base: {
        tableLayout: "fixed"
      },
      body: {
        padding: 1
      },
      sentDate: {
        width: "15%"
      },
      title: {
        width: "65%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
      to: {
        width: "20%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }
    },
    pagination: {
      width: "100%"
    }
  },
  errorMessage: {
    margin: 0,
    fontFamily: "mplus-1c-regular"
  },
}